import { getAppClientData, getDiscovery } from '@/services/app-client.client';
import type { GeneralDiscovery, KsAppClientDataAndMeta } from '@knapsack/types';
import { extractMetaState } from '../site/utils/extract-meta-state';
import type { ContentSrc, Env } from './env-and-content-src.types';

export function getEnvWebsocketUrl({
  envUrl,
  websocketsPort,
}: {
  envUrl: string | URL;
  websocketsPort: string | number;
}): string | undefined {
  if (!websocketsPort) return undefined;
  const url = typeof envUrl === 'string' ? new URL(envUrl) : envUrl;
  const protocol = url.protocol === 'https:' ? 'wss' : 'ws';
  return `${protocol}://${url.hostname}:${websocketsPort}`;
}

export function extractPossibleInstanceIdFromSite(site: {
  contentSrc: ContentSrc;
}): string | null {
  if (!site) return null;
  if (site.contentSrc.type !== 'cloud-authoring') return null;
  if (site.contentSrc.instance.type !== 'branch') return null;
  return site.contentSrc.instance.instanceId;
}

export function getEnvDetailsFromAppClientData({
  appClientData,
  envUrl,
  prodEnvUrl,
  discovery,
}: {
  appClientData: KsAppClientDataAndMeta;
  envUrl: string;
  prodEnvUrl: string;
  discovery: GeneralDiscovery;
}): Env | Error {
  try {
    // If we have a discovery, then we have these features
    const hasInspectEndpoint = !!discovery;
    const hasCodeSrcs = !!discovery;
    const hasDemoWithTemplateInfoSupport = !!discovery;

    const url = new URL(envUrl);

    const renderersMeta =
      discovery?.renderersById ?? appClientData.patternsState.renderers;
    const assetSetsState = discovery?.assetSets ?? appClientData.assetSetsState;
    const appClientMeta = extractMetaState(appClientData.metaState);

    if (appClientMeta.mode === 'start') {
      return {
        type: 'development',
        url: url.toString(),
        appClientMeta,
        renderersMeta,
        assetSetsState,
        websocketsEndpoint: getEnvWebsocketUrl({
          envUrl,
          websocketsPort: appClientMeta.websocketsPort,
        }),
        discovery,
        hasInspectEndpoint,
        hasCodeSrcs,
        hasDemoWithTemplateInfoSupport,
      };
    }

    return {
      url: url.toString(),
      appClientMeta,
      renderersMeta,
      assetSetsState,
      type:
        // For new workspaces the Env types `development` and `preview` will be used before `production` - so `prodEnvUrl` may not be set up yet
        prodEnvUrl && url.toString() === new URL(prodEnvUrl).toString()
          ? 'production'
          : 'preview',
      discovery,
      hasInspectEndpoint,
      hasCodeSrcs,
      hasDemoWithTemplateInfoSupport,
    };
  } catch (e) {
    return e;
  }
}

export async function getEnvDetails({
  envUrl,
  prodEnvUrl,
}: {
  envUrl: string;
  prodEnvUrl: string;
}): Promise<Env | Error> {
  if (!envUrl) {
    return new Error('No appClientUrl is provided');
  }
  const [discoveryResult, appClientData] = await Promise.all([
    getDiscovery({ appClientUrl: envUrl }),
    getAppClientData({
      appClientUrl: envUrl,
    }),
  ]);
  const discovery =
    discoveryResult.type === 'success' ? discoveryResult.data : null;
  return getEnvDetailsFromAppClientData({
    appClientData,
    envUrl,
    prodEnvUrl,
    discovery,
  });
}
